import React, { useState } from "react";
import { useNavigate, Navigate, Link as RouterLink } from "react-router-dom";
import toast from "../../components/ds/composite/Toast";
import {
  Box,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Container,
  Paper,
  Link,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Security as SecurityIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { THEME_CONSTANTS, commonStyles } from "../../theme/constants";
import { getBestLandingPage } from "../../utils/navigationUtils";
import { markStart, markEnd, FLOWS } from "../../utils/performanceMarks";

type LoginStep = "credentials" | "mfa";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [step, setStep] = useState<LoginStep>("credentials");
  const [mfaChallengeToken, setMfaChallengeToken] = useState<string | null>(
    null,
  );
  const [mfaCode, setMfaCode] = useState("");

  const {
    login,
    completeMfaLogin,
    isAuthenticated,
    entitlementIds,
    user,
    loading: authLoading,
  } = useAuth();
  const navigate = useNavigate();

  // While auth is still being checked (e.g. restoring session from storage /
  // validating token) do NOT render the login form — the user may already be
  // authenticated in another tab and we should redirect rather than flash the
  // login UI at them.
  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  // Redirect if already authenticated - go to best landing page based on entitlements
  if (isAuthenticated) {
    const landingPage = getBestLandingPage(
      entitlementIds,
      user?.role,
      user?.tenantCategory?.name,
    );
    return <Navigate to={landingPage} replace />;
  }

  const resolveEntitlementIdsFromUser = (entitlements: unknown): number[] => {
    if (!Array.isArray(entitlements)) return [];
    return entitlements
      .map((e) => {
        if (typeof e === "number") return e;
        if (typeof e === "string") return parseInt(e, 10) || 0;
        if (typeof e === "object" && e !== null && "id" in e)
          return (e as { id: number }).id;
        return 0;
      })
      .filter(Boolean);
  };

  const entitlementIdsForLanding = (r: {
    entitlements?: number[];
    user?: { entitlements?: unknown; role?: string } | null;
  }): number[] => {
    if (r.entitlements?.length) return r.entitlements;
    return resolveEntitlementIdsFromUser(r.user?.entitlements);
  };

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      markStart(FLOWS.LOGIN);
      const outcome = await login(email, password);

      if (outcome.kind === "mfa_required") {
        setMfaChallengeToken(outcome.mfaChallengeToken);
        setStep("mfa");
        setMfaCode("");
        return;
      }

      if (outcome.kind === "mfa_enrollment_pending") {
        toast.success(
          "Your organization requires two-factor authentication. Set up your authenticator in Account security.",
          { duration: 6000 },
        );
        navigate("/account/security", {
          replace: true,
          state: { enrollMfa: true },
        });
        return;
      }

      const response = outcome.response;
      const loginEntitlementIds = entitlementIdsForLanding(response);
      const userRole = response.user?.role;

      const landingPage = getBestLandingPage(
        loginEntitlementIds.length ? loginEntitlementIds : entitlementIds,
        userRole,
        response.user?.tenantCategory?.name,
      );
      markEnd(FLOWS.LOGIN);
      navigate(landingPage, { replace: true });
    } catch (err) {
      console.error("Login error:", err);
      if (err instanceof Error) {
        setError(err.message || "Login failed");
      } else {
        setError("Invalid email or password");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaChallengeToken || !mfaCode.trim()) {
      setError("Enter the code from your authenticator or a backup code.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await completeMfaLogin(
        mfaChallengeToken,
        mfaCode.trim(),
      );
      const loginEntitlementIds = entitlementIdsForLanding(response);
      const userRole = response.user?.role;
      const landingPage = getBestLandingPage(
        loginEntitlementIds.length ? loginEntitlementIds : entitlementIds,
        userRole,
        response.user?.tenantCategory?.name,
      );
      navigate(landingPage, { replace: true });
    } catch (err) {
      console.error("MFA verify error:", err);
      if (err instanceof Error) {
        setError(err.message || "Verification failed");
      } else {
        setError("Invalid code");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackFromMfa = () => {
    setStep("credentials");
    setMfaChallengeToken(null);
    setMfaCode("");
    setError("");
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const isMfaStep = step === "mfa";

  // Theme the MUI form controls to the DS teal accent instead of MUI's default
  // primary (blue/violet) so the login matches the rest of the app.
  const fieldAccentSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: THEME_CONSTANTS.borderRadius.medium,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--accent)",
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "var(--accent)" },
  } as const;

  const accentButtonSx = {
    py: THEME_CONSTANTS.spacing.sm,
    borderRadius: THEME_CONSTANTS.borderRadius.medium,
    textTransform: "none" as const,
    fontSize: "1rem",
    fontWeight: 600,
    minHeight: THEME_CONSTANTS.buttonHeight.large,
    bgcolor: "var(--accent)",
    color: "var(--accent-ink)",
    boxShadow: "none",
    transition: "filter 120ms ease",
    "&:hover": {
      bgcolor: "var(--accent)",
      filter: "brightness(0.93)",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      bgcolor: "var(--bg-sunken)",
      color: "var(--ink-3)",
    },
  } as const;

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: "100vh",
        ...commonStyles.flexCenter,
        p: THEME_CONSTANTS.spacing.md,
      }}
    >
      <Paper
        elevation={THEME_CONSTANTS.elevation.modal}
        sx={{
          width: "100%",
          maxWidth: 480,
          borderRadius: THEME_CONSTANTS.borderRadius.large,
          overflow: "hidden",
          boxShadow: THEME_CONSTANTS.shadows.modal,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "var(--bg-sunken)",
            color: "var(--ink-1)",
            borderBottom: "1px solid var(--line)",
            p: {
              xs: THEME_CONSTANTS.spacing.lg,
              sm: THEME_CONSTANTS.spacing.xl,
            },
            textAlign: "center",
          }}
        >
          {isMfaStep ? (
            <SecurityIcon
              sx={{
                fontSize: THEME_CONSTANTS.iconSize.xlarge,
                mb: THEME_CONSTANTS.spacing.sm,
              }}
            />
          ) : (
            <Box
              component="img"
              src="/logo.webp"
              alt="Alpha Health Logo"
              sx={{
                height: 80,
                width: "auto",
                maxWidth: 260,
                display: "block",
                mx: "auto",
                objectFit: "contain",
                mb: THEME_CONSTANTS.spacing.sm,
              }}
            />
          )}
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: { xs: "0.875rem", sm: "1rem" },
              opacity: 0.9,
            }}
          >
            Continuous Compliance / Case Management
          </Typography>
        </Box>

        {/* Login Form */}
        <CardContent
          sx={{
            p: {
              xs: THEME_CONSTANTS.spacing.lg,
              sm: THEME_CONSTANTS.spacing.xl,
            },
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            textAlign="center"
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              fontWeight: 600,
              mb: THEME_CONSTANTS.spacing.xs,
            }}
          >
            {isMfaStep ? "Two-factor authentication" : "Sign In"}
          </Typography>

          <Typography
            variant="body2"
            textAlign="center"
            sx={{
              color: "var(--ink-3)",
              mb: THEME_CONSTANTS.spacing.md,
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            {isMfaStep
              ? "Enter the 6-digit code from your authenticator app, or a backup code."
              : "Enter your credentials to access your account"}
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: THEME_CONSTANTS.spacing.md,
                borderRadius: THEME_CONSTANTS.borderRadius.medium,
              }}
            >
              {error}
            </Alert>
          )}

          {isMfaStep ? (
            <form onSubmit={handleMfaSubmit}>
              <TextField
                fullWidth
                label="Authenticator or backup code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                margin="normal"
                required
                autoComplete="one-time-code"
                autoFocus
                disabled={loading}
                inputProps={{
                  inputMode: "text",
                  autoCapitalize: "none",
                  spellCheck: false,
                }}
                sx={{ mb: THEME_CONSTANTS.spacing.md, ...fieldAccentSx }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !mfaCode.trim()}
                sx={{ mb: THEME_CONSTANTS.spacing.sm, ...accentButtonSx }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Verify"
                )}
              </Button>
              <Button
                type="button"
                fullWidth
                variant="text"
                disabled={loading}
                onClick={handleBackFromMfa}
                sx={{ textTransform: "none", color: "var(--accent)" }}
              >
                Back to sign in
              </Button>
            </form>
          ) : (
            <form onSubmit={handleCredentialsSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                autoComplete="email"
                autoFocus
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: "var(--ink-3)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: THEME_CONSTANTS.spacing.sm, ...fieldAccentSx }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                autoComplete="current-password"
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: "var(--ink-3)" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        disabled={loading}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        sx={{ color: "var(--ink-3)" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: THEME_CONSTANTS.spacing.md, ...fieldAccentSx }}
              />

              <Box sx={{ textAlign: "right", mb: THEME_CONSTANTS.spacing.sm }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  variant="body2"
                  sx={{
                    color: "var(--accent)",
                    textDecorationColor: "var(--accent)",
                    fontWeight: 500,
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !email || !password}
                sx={{ mb: THEME_CONSTANTS.spacing.sm, ...accentButtonSx }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Paper>
    </Container>
  );
};

export default Login;
